/**
 * Timeline Context
 *
 * Context is split into two focused contexts — import from those directly:
 *   useTimelineStaticContext()      — layout, config, dimensions (stable, not hover-driven)
 *   useTimelineInteractionContext() — hoverData, hoveredPhaseKey, hoveredTaskKey, hoveredReportIndex
 *
 * Use the useTimelineContextDispatch() hook to dispatch actions.
 */

import { createContext, useContext } from 'react'
import { TIMELINE_CONTEXT_ACTION_TYPES } from './timeline-context-reducer'

export const TimelineContextDispatch = createContext(null)

/**
 * useTimelineContextDispatch - Access timeline dispatch
 *
 * @throws {Error} If used outside TimelineContextProvider
 * @returns {Function} Timeline dispatch function
 */
export const useTimelineContextDispatch = () => {
  const dispatch = useContext(TimelineContextDispatch)

  if (!dispatch) {
    throw new Error('useTimelineContextDispatch must be used within TimelineContextProvider')
  }

  return dispatch
}

// Dispatch Helpers

export const setExpandedIds = ({ expandedIds }) => {
  return {
    type: TIMELINE_CONTEXT_ACTION_TYPES.SET_EXPANDED_IDS,
    expandedIds,
  }
}

export const setHoverData = ({ hoverData }) => {
  return {
    type: TIMELINE_CONTEXT_ACTION_TYPES.SET_HOVER_DATA,
    hoverData,
  }
}

export const setHoveredPhaseKey = ({ hoveredPhaseKey }) => {
  return {
    type: TIMELINE_CONTEXT_ACTION_TYPES.SET_HOVERED_PHASE_KEY,
    hoveredPhaseKey,
  }
}

export const setActivePhaseKey = ({ activePhaseKey }) => {
  return {
    type: TIMELINE_CONTEXT_ACTION_TYPES.SET_ACTIVE_PHASE_KEY,
    activePhaseKey,
  }
}

export const setWeekContainerWidth = ({ weekContainerWidth }) => {
  return {
    type: TIMELINE_CONTEXT_ACTION_TYPES.SET_WEEK_CONTAINER_WIDTH,
    weekContainerWidth,
  }
}

export const setGroupStrategy = ({ groupStrategy }) => {
  return {
    type: TIMELINE_CONTEXT_ACTION_TYPES.SET_GROUP_STRATEGY,
    groupStrategy,
  }
}

export const setSortStrategy = ({ sortStrategy }) => {
  return {
    type: TIMELINE_CONTEXT_ACTION_TYPES.SET_SORT_STRATEGY,
    sortStrategy,
  }
}

export const setContainerWidth = ({ containerWidth }) => {
  return {
    type: TIMELINE_CONTEXT_ACTION_TYPES.SET_CONTAINER_WIDTH,
    containerWidth,
  }
}

export const setViewStrategy = ({ viewStrategy }) => {
  return {
    type: TIMELINE_CONTEXT_ACTION_TYPES.SET_VIEW_STRATEGY,
    viewStrategy,
  }
}

export const setHoveredTaskKey = ({ hoveredTaskKey }) => {
  return {
    type: TIMELINE_CONTEXT_ACTION_TYPES.SET_HOVERED_TASK_KEY,
    hoveredTaskKey,
  }
}

export const setActiveTaskKey = ({ activeTaskKey }) => {
  return {
    type: TIMELINE_CONTEXT_ACTION_TYPES.SET_ACTIVE_TASK_KEY,
    activeTaskKey,
  }
}

export const setShowSuccessions = ({ showSuccessions }) => {
  return {
    type: TIMELINE_CONTEXT_ACTION_TYPES.SET_SHOW_SUCCESSIONS,
    showSuccessions,
  }
}

export const setShowOrgTasks = ({ showOrgTasks }) => {
  return {
    type: TIMELINE_CONTEXT_ACTION_TYPES.SET_SHOW_ORG_TASKS,
    showOrgTasks,
  }
}

export const setExpandedSuccessionKey = ({ expandedSuccessionKey }) => {
  return {
    type: TIMELINE_CONTEXT_ACTION_TYPES.SET_EXPANDED_SUCCESSION_KEY,
    expandedSuccessionKey,
  }
}

export const setHoveredReportIndex = ({ hoveredReportIndex }) => {
  return {
    type: TIMELINE_CONTEXT_ACTION_TYPES.SET_HOVERED_REPORT_INDEX,
    hoveredReportIndex,
  }
}

